import { useMemo, useState } from 'react';
import { AlertTriangle, CalendarX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LEADFLOW_RESOURCES_URL } from '@/components/cockpit/talk-track';
import {
  calendarView,
  formatDayLabel,
  formatMonthLabel,
  formatSlotTime,
  formatWindow,
  groupSlotsByDay,
  safeTimeZone,
  timeZoneOptions,
} from '@/cockpit/availability';
import type { CalendarStatus, SellerAvailability } from '@/cockpit/protocol';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Nothing on this pane holds or reserves anything, and a highlighted slot looks
// exactly like a booking widget — so it is said plainly, and again at the moment
// of the click.
const REFERENCE_NOTE =
  'Calendar is for your reference only. You must still book in the meeting on Leadflow';

export function CalendarUnavailable() {
  return (
    <div className="text-muted-foreground flex flex-col items-center gap-2 py-10 text-center text-sm">
      <CalendarX className="h-5 w-5" />
      <p>Calendar unavailable right now — agree a time and book it in Leadflow as usual.</p>
    </div>
  );
}

export function SellerCalendar({
  status,
  calendar,
  leadTimeZone,
}: {
  status: CalendarStatus;
  calendar: SellerAvailability | null;
  leadTimeZone: string;
}) {
  const zones = useMemo(() => timeZoneOptions(leadTimeZone), [leadTimeZone]);
  const [timeZone, setTimeZone] = useState(() => safeTimeZone(leadTimeZone, 'UTC'));
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [month, setMonth] = useState<string | null>(null);

  const days = useMemo(
    () => groupSlotsByDay(calendar?.slots ?? [], timeZone),
    [calendar?.slots, timeZone],
  );
  const view = useMemo(
    () => calendarView(days, calendar?.window ?? { start: '', end: '' }, selectedDay, month),
    [days, calendar?.window, selectedDay, month],
  );

  if (status !== 'ready' || !calendar) return <CalendarUnavailable />;

  const union = !calendar.matched;
  const { shownMonth, monthIndex, months, day, cells, slots } = view;
  const windowLabel = formatWindow(calendar.window);
  // A marked slot can leave the view when the zone changes. The note below must
  // not outlive the thing it points at.
  const picked = slots.some((s) => s.startUtc === selectedSlot) ? selectedSlot : null;

  return (
    <div className="space-y-3" data-testid="seller-calendar">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          <span className="font-medium" data-testid="calendar-owner">
            Seller availability
          </span>
        </div>
        <select
          value={timeZone}
          onChange={(e) => {
            setTimeZone(safeTimeZone(e.target.value, timeZone));
            setSelectedSlot(null);
          }}
          aria-label="Show times in"
          data-testid="calendar-timezone"
          className="border-input bg-background rounded-md border px-2 py-1 text-sm"
        >
          {zones.map((zone) => (
            <option key={zone.id} value={zone.id}>
              {zone.label}
            </option>
          ))}
        </select>
      </div>

      {calendar.stale && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid="calendar-stale"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>These times were last synced a while ago and may be out of date.</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          disabled={monthIndex <= 0}
          onClick={() => {
            setMonth(months[monthIndex - 1] ?? shownMonth);
            setSelectedSlot(null);
          }}
          aria-label="Previous month"
          className="text-muted-foreground rounded px-2 py-1 text-sm disabled:opacity-30"
          data-testid="calendar-prev-month"
        >
          ‹
        </button>
        <span className="text-sm font-medium">{formatMonthLabel(shownMonth)}</span>
        <button
          type="button"
          disabled={monthIndex < 0 || monthIndex >= months.length - 1}
          onClick={() => {
            setMonth(months[monthIndex + 1] ?? shownMonth);
            setSelectedSlot(null);
          }}
          aria-label="Next month"
          className="text-muted-foreground rounded px-2 py-1 text-sm disabled:opacity-30"
          data-testid="calendar-next-month"
        >
          ›
        </button>
      </div>

      {/* Room for six rows always, so paging from a five-row month to a six-row
          one doesn't move everything below the grid. */}
      <div className="grid min-h-[18rem] grid-cols-7 content-start gap-1 text-center">
        {WEEKDAYS.map((weekday) => (
          <div key={weekday} className="text-muted-foreground pb-1 text-xs">
            {weekday}
          </div>
        ))}
        {cells.map((cell, index) => (
          <button
            key={cell.date ?? `pad-${index}`}
            type="button"
            disabled={!cell.selectable}
            onClick={() => {
              setSelectedDay(cell.date);
              setSelectedSlot(null);
            }}
            aria-current={cell.date === day ? 'date' : undefined}
            aria-label={
              cell.date
                ? `${formatDayLabel(cell.date)}, ${cell.slotCount} open ${
                    cell.slotCount === 1 ? 'time' : 'times'
                  }`
                : undefined
            }
            data-testid={cell.date ? `calendar-day-${cell.date}` : undefined}
            className={cn(
              'flex h-11 flex-col items-center justify-center rounded-md border text-sm',
              cell.date === null && 'invisible',
              cell.selectable
                ? 'hover:bg-muted border-border cursor-pointer'
                : 'text-muted-foreground/50 cursor-default border-transparent',
              cell.date === day && 'bg-primary text-primary-foreground border-primary',
            )}
          >
            <span>{cell.day || ''}</span>
            {cell.slotCount > 0 && (
              <span className="text-[10px] tabular-nums opacity-80">{cell.slotCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* The grid can't distinguish a day the seller is full from one we never
          asked about — both are simply not clickable. This says which is which. */}
      <p className="text-muted-foreground text-xs" data-testid="calendar-window">
        {windowLabel && `Availability shown for ${windowLabel}. `}
        {REFERENCE_NOTE}.
      </p>

      <div className="space-y-1">
        {day === null ? (
          <p
            className="text-muted-foreground py-4 text-center text-sm"
            data-testid="calendar-empty"
          >
            No open times in this window — agree a time and book it in Leadflow as usual.
          </p>
        ) : (
          <>
            <p className="text-sm font-medium">{formatDayLabel(day)}</p>
            {/* Scrolls rather than grows: a day with a dozen open slots must not
                push the end-call controls down the page. */}
            <div className="grid max-h-28 grid-cols-3 gap-1 overflow-y-auto">
              {slots.map((slot) => {
                const freeCount = slot.freeSellerCount;
                const thin = union && freeCount != null && freeCount > 0 && freeCount <= 2;
                const time = formatSlotTime(slot.startUtc, timeZone);
                return (
                  <button
                    key={slot.startUtc}
                    type="button"
                    aria-pressed={slot.startUtc === picked}
                    aria-label={union && freeCount != null ? `${time}, ${freeCount} free` : time}
                    onClick={() => setSelectedSlot(slot.startUtc === picked ? null : slot.startUtc)}
                    data-testid={`calendar-slot-${slot.startUtc}`}
                    className={cn(
                      'rounded-md border px-2 py-1.5 text-sm tabular-nums',
                      slot.startUtc === picked
                        ? 'border-primary bg-primary/10 font-medium'
                        : 'hover:bg-muted',
                      // Weakened by its border, not its text colour: muted text is
                      // what an unclickable day looks like, and these are bookable.
                      thin && slot.startUtc !== picked && 'border-dashed',
                    )}
                  >
                    {time}
                    {union && freeCount != null && (
                      <span className="ml-1 text-[10px] opacity-70">{freeCount} free</span>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Holds its space whether or not a slot is marked. Appearing on click
          would push the end-call controls down at the exact moment the SDR is
          reaching for them. */}
      <div className="min-h-[3.25rem]">
        {picked && (
          <p
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            data-testid="calendar-booking-note"
          >
            {REFERENCE_NOTE} —{' '}
            <a
              href={LEADFLOW_RESOURCES_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline underline-offset-4"
            >
              open Leadflow ↗
            </a>
          </p>
        )}
      </div>
    </div>
  );
}

// The count the tab badges, so an SDR can see whether switching is worth it
// without switching. Null when there is nothing to advertise.
export function slotCountBadge(calendar: SellerAvailability | null): string | null {
  if (!calendar) return null;
  const total = calendar.slots.length;
  return total > 0 ? String(total) : null;
}
